import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaChevronDown, FaChevronUp, FaMicrophoneAlt } from 'react-icons/fa';

import { isJapanTraffic } from '../../../_helpers/japanTraffic.js';
import { parseLyricSheet, withoutChordOnlyLines } from '../../../_helpers/lyricSheet.js';
import { LyricsVoice, unavailableKey } from '../../../_helpers/lyricsMessages.js';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { useLyricsImpression } from '../../../hooks/useLyricsImpression.js';
import { LyricsStatus, isProviderAnswer } from '../../../hooks/useSongLyrics.js';
import LyricsAttribution from '../lyrics/LyricsAttribution.jsx';
import '../../../styles/StreamLyricsPanel.css';

/*
 * Sing-along lyrics for the song on stage (ENG-415). The watch page hands
 * over the playing queue entry and whatever `useSongLyrics` resolved for it
 * from `GET /songs/{id}/lyrics`, already normalized off the wire, and only
 * while the stream is live; the panel follows that entry and goes away
 * between songs, so listeners never read the previous song's words.
 *
 * The "song on stage" signal is the watch page's queue poll, so a song change
 * reaches the panel within one poll interval (QUEUE_POLL_INTERVAL_MS, 10s)
 * rather than instantly — the status socket the page holds open carries
 * stream status, viewer count and show metadata, but no now-playing event.
 *
 * ## The display obligations are the feature
 *
 * Almost all of Musixmatch's go-live checklist lands here rather than on the
 * backend, and none of it is optional — it is the licence the words are
 * served under, and ENG-414 closes by noting the endpoint "is only compliant
 * once [the client] exists". Whenever a provider body is on screen this panel
 * must therefore show:
 *
 *   - the `copyright` string, beside the lyrics;
 *   - the "powered by Musixmatch" credit, linked to `backlink_url`;
 *   - `script_tracking_url` loaded as a script tag — this is how the
 *     impression is counted, and counting it is the condition the body is
 *     served to us on.
 *
 * The first two are unconditional for a provider body and are `LyricsAttribution`'s
 * to render, here and on the performer's stand alike; the impression is
 * `useLyricsImpression`'s, for the same reason. The tracking script is the one
 * obligation with no stand-in — there is nothing to substitute for a URL we
 * were not given.
 *
 * Two more that are not about attribution: copy and paste is disabled for
 * traffic originating in Japan (`isJapanTraffic`), and a `restricted` answer
 * shows the message the provider supplied rather than an empty panel.
 *
 * None of it applies to the band's own text out of `songs.lyrics`, which has
 * no third party to credit and no impression to count. Which of the two an
 * answer is, is `isProviderAnswer`'s to decide and not this file's: the
 * backend's `source` is 'musixmatch' or 'catalogue' (see useSongLyrics), and
 * any other value needs positive provider evidence rather than the absence
 * of a catalogue marker. Getting that backwards would print a Musixmatch
 * licence over words a band typed themselves.
 *
 * `pixel_tracking_url` comes back on the same payload and is not rendered
 * alongside the script: it is the no-JavaScript alternative to it, and firing
 * both would report two impressions for one view. It is used only when the
 * script fails to load, which on this page mostly means an ad blocker ate it.
 *
 * ## Two kinds of text
 *
 * A provider body is prose: rendered line for line, exactly as sent, notice
 * and all (the "not for commercial use" sentence is part of what we are
 * licensed to show, not noise to tidy away). A catalogue body is whatever a
 * musician typed into the song editor, which in this app is a chord sheet —
 * so it goes through the same parser the performance stand uses
 * (`parseLyricSheet`), minus the chord-only lines (`withoutChordOnlyLines`):
 * the audience wants the words, so inline ChordPro chords and chord-over-
 * lyric lines come through as plain sung text rather than a chart. Section
 * labels the band typed ("[Chorus]") are kept because they help someone
 * follow along; the ones the parser makes up ("Part 1", carried as
 * `labelKey`) are scaffolding for the performer's section rail.
 */

/** The sung text of one parsed line; '' for a line that carried only chords. */
const lineText = (segments) => segments.map((segment) => segment.lyric).join('').trim();

/** A provider body: one section of plain lines, blank ones kept as breaks. */
const providerSections = (body) => [
  {
    id: 'provider',
    label: null,
    lines: body.replace(/\r\n?/g, '\n').split('\n').map((line) => line.trimEnd()),
  },
];

/** A band's own sheet: parsed, de-chorded, and stripped of invented labels. */
const catalogueSections = (body, t) =>
  withoutChordOnlyLines(parseLyricSheet(body, t)).map((section) => ({
    id: section.id,
    label: section.labelKey ? null : section.label,
    lines: section.lines.map(lineText),
  }));

/*
 * Someone who hid the lyrics is not asking to be shown them again every time
 * the queue advances, the panel remounts, or they reload.
 * sessionStorage is where this app already keeps per-session preferences
 * (see ThemeContext); it can throw when storage is blocked, so both ends
 * fall back to "shown".
 */
const COLLAPSED_KEY = 'stream-lyrics-collapsed';

const readCollapsed = () => {
  try {
    return sessionStorage.getItem(COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
};

const writeCollapsed = (collapsed) => {
  try {
    sessionStorage.setItem(COLLAPSED_KEY, String(collapsed));
  } catch {
    /* preference is not worth failing a render over */
  }
};

/** Marks the provider's words where copying them is blocked. */
const NO_COPY_CLASS = 'stream-lyrics-section--no-copy';

/**
 * Whether a copy, cut or drag would take any of the guarded words: it starts
 * inside them, or the current selection reaches into them.
 */
const reachesGuardedWords = (root, target) => {
  if (!root) return false;
  const guarded = [...root.querySelectorAll(`.${NO_COPY_CLASS}`)];
  if (guarded.length === 0) return false;
  if (target instanceof Node && guarded.some((node) => node.contains(target))) return true;
  const selection = document.getSelection?.();
  if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return false;
  const range = selection.getRangeAt(0);
  return guarded.some((node) => typeof range.intersectsNode === 'function' && range.intersectsNode(node));
};

const StreamLyricsPanel = ({ song, lyrics = null, status = LyricsStatus.IDLE, code = null }) => {
  const { t } = useLocale();
  const [isCollapsed, setIsCollapsed] = useState(readCollapsed);
  const bodyRef = useRef(null);

  // Not folded into the updater: StrictMode double-invokes those, and this
  // one touches storage.
  const toggleCollapsed = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    writeCollapsed(next);
  };

  // Whose words these are: the backend's `source`, and for any value it does
  // not define, evidence the payload actually carries. A provider
  // answer stripped of its attribution is still a provider answer and still
  // owes the credit; an unlabelled body with nothing of the provider's on it
  // is the band's own, and is owed no licence claim over it.
  const isProvider = isProviderAnswer(lyrics, { restricted: status === LyricsStatus.RESTRICTED });

  const sections = useMemo(() => {
    if (status !== LyricsStatus.READY) return [];
    const body = typeof lyrics?.body === 'string' ? lyrics.body : '';
    const parsed = isProvider ? providerSections(body) : catalogueSections(body, t);
    return parsed.filter((section) => section.lines.some((line) => line !== ''));
  }, [status, lyrics, isProvider, t]);

  /*
   * A body that parsed to nothing — a "lyrics" field holding only a chord
   * progression, a 200 of whitespace — is READY with no words in it. That is
   * the same news as no lyrics at all, and it gets the same sentence: the
   * panel never shows a heading over an empty box.
   */
  const parsedWords = sections.length > 0;
  /*
   * An excerpt is licensed on the condition that the rest of the song is one
   * link away. The backend fills `backlink_url` from `track_url` when it can,
   * but both can arrive blank, and an excerpt with no way to the rest is not
   * ours to show (ENG-521): it gets the "no lyrics" sentence instead.
   */
  const isUnlinkedExcerpt =
    status === LyricsStatus.READY && parsedWords && Boolean(lyrics?.partial) && !lyrics?.backlinkUrl;
  const hasWords = parsedWords && !isUnlinkedExcerpt;
  const isEmptyAnswer = status === LyricsStatus.READY && !hasWords;

  useEffect(() => {
    if (isUnlinkedExcerpt) {
      console.warn('Lyrics excerpt arrived without a backlink; not showing it.', { songId: lyrics?.songId ?? null });
    }
  }, [isUnlinkedExcerpt, lyrics?.songId]);

  // A new song starts at its first line, however far the last one was read
  const songKey = song?.SongId ?? song?.RequestId ?? null;
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [songKey, status]);

  /*
   * The impression Musixmatch counts, and the condition the body is served
   * to us on. A view begins when a body is on screen — not merely fetched: a
   * listener who collapsed the panel is not reading lyrics, and counting them
   * would claim an impression we did not show.
   */
  const trackingUrl = hasWords && isProvider ? lyrics?.trackingScriptUrl ?? null : null;
  useLyricsImpression(trackingUrl, {
    pixelUrl: lyrics?.trackingPixelUrl ?? null,
    visible: !isCollapsed,
  });

  // Read per render rather than once per module so a reader who crosses a
  // time zone mid-session is judged on where they are now. Only the
  // provider's words are covered (ENG-521), as on the stand: a band's own
  // lyrics, the copyright line and the credit stay copyable everywhere.
  const blockCopying = hasWords && isProvider && isJapanTraffic();
  // Copy, cut and drag only. Cancelling the context menu as well would take
  // away translate, search, back and "open in new tab" from these readers,
  // and on a touch device the long-press menu is most of what there is; the
  // obligation is about copying the lyrics, not about the browser's menu.
  const preventCopying = blockCopying
    ? (event) => { if (reachesGuardedWords(bodyRef.current, event.target)) event.preventDefault(); }
    : undefined;

  /*
   * A selection that starts outside the words and runs into them, or a
   * select-all, raises `copy` on an element that is not inside them — for
   * select-all, on `body`, outside React's tree. So the guard also listens
   * where those land, and cancels only a copy that reaches the words.
   */
  useEffect(() => {
    if (!blockCopying) return undefined;
    const guard = (event) => {
      if (reachesGuardedWords(bodyRef.current, event.target)) event.preventDefault();
    };
    document.addEventListener('copy', guard);
    document.addEventListener('cut', guard);
    return () => {
      document.removeEventListener('copy', guard);
      document.removeEventListener('cut', guard);
    };
  }, [blockCopying]);

  /*
   * Attribution rides on the answer, not on the body: a restricted response
   * carries the copyright and the backlink with no words at all, and the
   * credit still applies to it. It is owed for anything of the provider's
   * that is on screen — so the credit appears with the lyrics, and with the
   * restriction notice, and never over an empty panel or over our own
   * "no lyrics for this song" sentence, which are nobody's words to licence.
   */
  const hasProviderAnswer =
    isProvider && (status === LyricsStatus.RESTRICTED || (status === LyricsStatus.READY && hasWords));
  const copyright = lyrics?.copyright || null;
  const backlink = lyrics?.backlinkUrl || null;
  const isExcerpt = status === LyricsStatus.READY && hasWords && Boolean(lyrics?.partial);

  // The words are third-party text in whatever language they were written
  // in, not the interface language; the page is UTF-8 (index.html), so
  // scripts outside Latin-1 arrive intact and this is what tells a screen
  // reader how to pronounce them. It goes on the lyrics themselves and not
  // on the panel: the copyright line, the excerpt link and the credit are
  // our English and the provider's, and reading those out under a Japanese
  // track's `lang` is WCAG 3.1.2 the other way round.
  const bodyLanguage = (status === LyricsStatus.READY && lyrics?.language) || undefined;

  // Nothing to say at all: no song on stage, or lyrics were never asked for.
  if (!song || status === LyricsStatus.IDLE) return null;

  return (
    <section className="stream-lyrics" aria-label={t('stream.lyrics.label')} data-testid="stream-lyrics" data-status={status}>
      <div className="stream-lyrics-header">
        <span className="stream-lyrics-eyebrow">
          <FaMicrophoneAlt aria-hidden="true" /> {t('stream.lyrics.eyebrow')}
        </span>
        {/* Title only: OnStageBar sits directly above and already pairs it
            with the artist, so repeating the artist here says it twice. */}
        <span className="stream-lyrics-title">{song.Title}</span>
        <button
          type="button"
          className="stream-lyrics-toggle"
          aria-expanded={!isCollapsed}
          aria-controls="stream-lyrics-body"
          onClick={toggleCollapsed}
        >
          {isCollapsed ? <FaChevronDown aria-hidden="true" /> : <FaChevronUp aria-hidden="true" />}
          {isCollapsed ? t('stream.lyrics.show') : t('stream.lyrics.hide')}
        </button>
      </div>

      {/* The body stays mounted while hidden so the toggle's `aria-controls`
          always resolves. It scrolls, so it is a focus stop in its own right:
          without `tabIndex` a keyboard or switch user reads only the lines
          that happen to fit (WCAG 2.1.1), the same reason the performance
          stand's sheet is focusable. */}
      <div
        className="stream-lyrics-body"
        id="stream-lyrics-body"
        ref={bodyRef}
        hidden={isCollapsed}
        tabIndex={0}
        role="group"
        aria-label={t('stream.lyrics.sheet')}
        onCopy={preventCopying}
        onCut={preventCopying}
        onDragStart={preventCopying}
      >
        {status === LyricsStatus.LOADING && (
          <p className="stream-lyrics-note" data-testid="stream-lyrics-note" role="status">{t('stream.lyrics.loading')}</p>
        )}

        {status === LyricsStatus.UNAVAILABLE && (
          <p className="stream-lyrics-note" data-testid="stream-lyrics-note">{t(unavailableKey(code, LyricsVoice.LISTENER))}</p>
        )}

        {/* An answer that turned out to have no words in it, said out loud
            rather than left as an empty box under a heading. */}
        {isEmptyAnswer && (
          <p className="stream-lyrics-note" data-testid="stream-lyrics-note">{t('stream.lyrics.unavailable.notFound')}</p>
        )}

        {/* The provider's own sentence for why, when it sent one: it says
            more than a generic refusal can, and showing it is the point of
            the flag. Our own wording stands in if it ever arrives empty. */}
        {status === LyricsStatus.RESTRICTED && (
          <p className="stream-lyrics-note" data-testid="stream-lyrics-note">
            {lyrics?.restrictionMessage || t('stream.lyrics.restricted')}
          </p>
        )}

        {hasWords && sections.map((section) => (
          <div
            key={section.id}
            className={`stream-lyrics-section${blockCopying ? ` ${NO_COPY_CLASS}` : ''}`}
            lang={bodyLanguage}
          >
            {section.label && <span className="stream-lyrics-section-label">{section.label}</span>}
            {section.lines.map((text, index) =>
              text === '' ? (
                <span key={index} className="stream-lyrics-break" aria-hidden="true" />
              ) : (
                <p key={index} className="stream-lyrics-line" data-testid="stream-lyrics-line">{text}</p>
              )
            )}
          </div>
        ))}

        {isExcerpt && backlink && (
          <p className="stream-lyrics-note stream-lyrics-excerpt">
            <a href={backlink} target="_blank" rel="noopener noreferrer">{t('stream.lyrics.excerpt')}</a>
          </p>
        )}

        {hasProviderAnswer && <LyricsAttribution copyright={copyright} backlinkUrl={backlink} />}
      </div>
    </section>
  );
};

export default StreamLyricsPanel;
